import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../context/LanguageContext';

export type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive styling for the confirm button */
  danger?: boolean;
};

type Pending = ConfirmOptions & {
  resolve: (ok: boolean) => void;
};

type ConfirmApi = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = React.createContext<ConfirmApi | null>(null);

export function useConfirm(): ConfirmApi {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<Pending | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();
  const { t } = useLanguage();

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve });
    });
  }, []);

  const close = useCallback((ok: boolean) => {
    setPending((prev) => {
      prev?.resolve(ok);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!pending) return;
    cancelRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [pending, close]);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {pending && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="fixed inset-0 z-[220] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-[2px]"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) close(false);
              }}
            >
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descId}
                className="w-full max-w-md rounded-lg border border-line dark:border-line-dark bg-white dark:bg-ink-elevated p-5 shadow-[var(--bc-shadow-md)] space-y-4"
              >
                <h2 id={titleId} className="font-display text-base font-bold text-ink dark:text-paper">
                  {pending.title}
                </h2>
                <p id={descId} className="text-sm text-fog leading-relaxed whitespace-pre-wrap">
                  {pending.message}
                </p>
                <div className="flex flex-wrap justify-end gap-2 pt-1">
                  <button
                    ref={cancelRef}
                    type="button"
                    className="bc-btn-ghost !text-xs"
                    onClick={() => close(false)}
                  >
                    {pending.cancelLabel || t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className={
                      pending.danger
                        ? 'inline-flex items-center justify-center rounded-md px-3 py-2 text-xs font-semibold bg-rose-600 text-white hover:bg-rose-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500'
                        : 'bc-btn-primary !text-xs'
                    }
                    onClick={() => close(true)}
                  >
                    {pending.confirmLabel || t('common.confirm')}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </ConfirmContext.Provider>
  );
};
